import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import CircularProgress from '@mui/material/CircularProgress';

// @mui
import {
    Box,
    Button,
    Card,
    Container,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableRow,
    Typography,
    Dialog,
    DialogTitle,
    DialogActions,
    Divider,
    Grid,
    TextField,
    MenuItem
} from '@mui/material';

import FormProvider from 'hooks/form/form-provider';
import { useForm, Controller } from 'react-hook-form';
import { countries } from '../hooks/form/countries';
import { RHFAutocomplete } from '../hooks/form/rhf-autocomplete';


import useResponsive from '../hooks/useResponsive';
import Scrollbar from '../components/scrollbar';
import { color, styled } from '@mui/system';
import { useSnackbar } from '../components/snackbar';
import { Add } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';


import {
    addSignature,
    getSignatureList,
    deleteSignature,
    updateSignature,
} from 'api/users';

//-----------------------------------------------------------------------
const OverflowTypography = styled(Typography)(() => ({
    maxWidth: 150,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
}));

export default function ManageSignature() {
    const { enqueueSnackbar } = useSnackbar();
    const isLg = useResponsive('up', 'md');

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [openAddSignature, setOpenAddSignature] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [editData, setEditData] = useState(null);


    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await getSignatureList();
            setLoading(false);
            if (response?.success) {
                setData(response?.data || []);
            } else {
                enqueueSnackbar(response.message, { variant: 'error' });
            }
        } catch (e) {
            setLoading(false);
            enqueueSnackbar(e.message, { variant: 'error' });
        }
    };

    const handleDelete = async (row) => {
        try {
            setIsSubmitting(true);
            await deleteSignature(row?._id);

            enqueueSnackbar('Signature deleted successfully', {
                variant: 'success',
            });

            fetchData();
        } catch (error) {
            enqueueSnackbar('Unable to delete signature', {
                variant: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <>
            <Helmet>
                <title>Manage Masters Data</title>
            </Helmet>

            <Container sx={{ mt: isLg ? 0 : 1 }}>
                <Stack direction="row" justifyContent="space-between" mb={2}>
                    <Typography variant="h4">
                        Manage Signature {data.length ? `(${data.length})` : ''}
                    </Typography>

                    <Button variant="contained" onClick={() => setOpenAddSignature(true)}>
                        <Add sx={{ mr: 1 }} /> Add Signature
                    </Button>

                </Stack>

                <Card>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Scrollbar>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Phone No.</TableCell>
                                        <TableCell>Signature</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                </TableHead>

                                {data.length ? (
                                    <TableBody>
                                        {data.map((row, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell>
                                                    <OverflowTypography>{row?.name || '—'}</OverflowTypography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {row?.type || '—'}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>{row?.phoneno}</TableCell>

                                                <TableCell>
                                                    {row?.sign_url ? (
                                                        <img
                                                            src={row.sign_url}
                                                            alt="signature"
                                                            style={{
                                                                height: 40,
                                                                cursor: "pointer",
                                                                borderRadius: 4,
                                                            }}
                                                            onClick={() => setPreviewImage(row.sign_url)}
                                                        />

                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No signature
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                <TableCell sx={{ display: 'flex', gap: 1 }}>
                                                    <LoadingButton
                                                        color="primary"
                                                        variant="contained"
                                                        onClick={() => {
                                                            setEditData(row);
                                                            setOpenAddSignature(true);
                                                        }}
                                                    >
                                                        Edit
                                                    </LoadingButton>

                                                    <LoadingButton
                                                        loading={isSubmitting}
                                                        color="error"
                                                        variant="contained"
                                                        onClick={() => handleDelete(row)}
                                                    >
                                                        Delete
                                                    </LoadingButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                ) : (
                                    <TableFooter>
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                No signature found.
                                            </TableCell>
                                        </TableRow>
                                    </TableFooter>
                                )}
                            </Table>
                        </Scrollbar>
                    )}
                </Card>
            </Container>
            <Dialog
                fullWidth
                maxWidth="xs"
                open={openAddSignature}
                onClose={() => {
                    setOpenAddSignature(false);
                    setEditData(null);
                }}
            >
                <AddSignatureComponent
                    editData={editData}
                    onClose={() => {
                        setOpenAddSignature(false);
                        setEditData(null);
                    }}
                    onSuccess={() => {
                        setOpenAddSignature(false);
                        setEditData(null);
                        fetchData();
                    }}
                />
            </Dialog>

            <Dialog
                open={Boolean(previewImage)}
                onClose={() => setPreviewImage(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        m: 0,
                        p: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    Signature Preview

                    <IconButton
                        onClick={() => setPreviewImage(null)}
                        size="small"
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Box sx={{ p: 3, textAlign: "center" }}>
                    {previewImage && (
                        <img
                            src={previewImage}
                            alt="Preview"
                            style={{
                                maxWidth: "100%",
                                maxHeight: "70vh",
                                borderRadius: 8,
                            }}
                        />
                    )}
                </Box>
            </Dialog>


        </>
    );
}

function AddSignatureComponent({ onClose, onSuccess, editData }) {
    const { enqueueSnackbar } = useSnackbar();
    const [preview, setPreview] = useState(null);

    const defaultValues = {
        country_code: '91',
        phone_number: editData?.phoneno || '',
        name: editData?.name || '',
        type: editData?.type || '',
        signature: null,
    };

    const methods = useForm({
        defaultValues,
        mode: 'onChange',
    });

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
    } = methods;

    // Reset form on edit
    useEffect(() => {
        if (editData) {
            reset({
                country_code: '91',
                phone_number: editData?.phoneno
                    ? editData.phoneno.replace(/^\+91/, '')
                    : '',
                name: editData?.name || '',
                type: editData?.type || '',
                signature: null,
            });

            if (editData?.sign_url) {
                setPreview(editData.sign_url);
            }
        }
    }, [editData, reset]);

    const onSubmit = handleSubmit(
        async (data) => {
            try {
                const formData = new FormData();

                // Append file
                if (data.signature) {
                    formData.append('file', data.signature);
                }

                // Append user_data as JSON string
                formData.append(
                    'user_data',
                    JSON.stringify({
                        name: data.name,
                        phoneno: `+${data.country_code}${data.phone_number}`,
                        type: data.type,
                    })
                );

                if (editData) {
                    await updateSignature(editData?._id, formData);
                } else {
                    await addSignature(formData);
                }

                enqueueSnackbar(
                    editData
                        ? 'Signature updated successfully'
                        : 'Signature added successfully',
                    { variant: 'success' }
                );

                onSuccess();
            } catch (error) {
                enqueueSnackbar('Failed to save signature', {
                    variant: 'error',
                });
            }
        },
        () => {
            enqueueSnackbar('Please fix the highlighted errors', {
                variant: 'error',
            });
        }
    );

    return (
        <>
            {/* Header */}
            <DialogTitle sx={{ pb: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                    {editData ? 'Edit Signature' : 'Add Signature'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {editData
                        ? 'Update the details below'
                        : 'Fill the details to add a new signature'}
                </Typography>
            </DialogTitle>

            <Divider />

            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Box
                    sx={{
                        px: 3,
                        py: 4,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Box sx={{ width: '100%', maxWidth: 700 }}>
                        <Stack spacing={4}>
                            {/* BASIC INFO */}
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    mb={2}
                                >
                                    Basic Information
                                </Typography>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="country_code"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    label="Country Code"
                                                    value="+91"
                                                    fullWidth
                                                    disabled
                                                />
                                            )}
                                        />

                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="phone_number"
                                            control={control}
                                            rules={{
                                                required: 'Phone number required',
                                                pattern: {
                                                    value: /^[0-9]{8,10}$/,
                                                    message: 'Enter valid phone number',
                                                },
                                            }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    label="Phone Number"
                                                    fullWidth
                                                    disabled={!!editData}
                                                    error={!!errors.phone_number}
                                                    helperText={errors.phone_number?.message}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="name"
                                            control={control}
                                            rules={{ required: 'Name required' }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    label="Full Name"
                                                    fullWidth
                                                    error={!!errors.name}
                                                    helperText={errors.name?.message}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="type"
                                            control={control}
                                            rules={{ required: 'Qualification is required' }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    select
                                                    label="Qualification"
                                                    fullWidth
                                                    error={!!errors.type}
                                                    helperText={errors.type?.message}
                                                >
                                                    <MenuItem value="DM Cardiologist">
                                                        DM Cardiologist
                                                    </MenuItem>
                                                    <MenuItem value="MD/DNB Medicine">
                                                        MD/DNB Medicine
                                                    </MenuItem>
                                                    <MenuItem value="CCT Expert">
                                                        CCT Expert
                                                    </MenuItem>
                                                </TextField>
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Divider />

                            {/* SIGNATURE */}
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    mb={2}
                                >
                                    Signature
                                </Typography>

                                <Controller
                                    name="signature"
                                    control={control}
                                    rules={{
                                        validate: (value) => {
                                            if (!editData && !value) {
                                                return 'Signature required';
                                            }
                                            if (editData && !preview && !value) {
                                                return 'Signature required';
                                            }

                                            return true;
                                        }
                                    }}

                                    render={({ field }) => {
                                        const handleFileChange = (e) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;

                                            field.onChange(file);
                                            setPreview(URL.createObjectURL(file));
                                        };

                                        const handleRemove = () => {
                                            setPreview(null);
                                            field.onChange(null);
                                        };

                                        return (
                                            <>
                                                {/* SHOW UPLOAD BOX ONLY IF NO PREVIEW */}
                                                {!preview && (
                                                    <Box
                                                        component="label"
                                                        sx={{
                                                            borderColor: 'divider',
                                                            borderRadius: 2,
                                                            p: 5,
                                                            textAlign: 'center',
                                                            cursor: 'pointer',
                                                            transition: '0.2s',

                                                        }}
                                                    >
                                                        <Typography variant="body2" color="text.secondary">
                                                            {editData
                                                                ? 'Click to Replace Signature'
                                                                : 'Click to Upload Signature'}
                                                        </Typography>

                                                        <input
                                                            hidden
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={handleFileChange}
                                                        />
                                                    </Box>
                                                )}

                                                {/* SHOW PREVIEW IF IMAGE EXISTS */}
                                                {preview && (
                                                    <Box
                                                        sx={{
                                                            mt: 3,
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                position: 'relative',
                                                                borderRadius: 2,
                                                                overflow: 'hidden',
                                                                maxWidth: 350,
                                                                width: '100%',
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                           
                                                            <img
                                                                src={preview}
                                                                alt="preview"
                                                                style={{
                                                                    width: '100%',
                                                                    height: 120,
                                                                    objectFit: 'contain',
                                                                    display: 'block',
                                                                }}
                                                            />

                                                            {/* HOVER OVERLAY */}
                                                            <Box
                                                                component="label"
                                                                sx={{
                                                                    position: 'absolute',
                                                                    inset: 0,
                                                                    bgcolor: 'rgba(0,0,0,0.45)',
                                                                    opacity: 0,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    transition: '0.2s',
                                                                    cursor: "pointer",
                                                                    '&:hover': {
                                                                        opacity: 1,
                                                                    },
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ color: '#fff', fontWeight: 500 }}
                                                                >
                                                                    ✏ Edit Signature
                                                                </Typography>

                                                                <input
                                                                    hidden
                                                                    type="file"
                                                                    accept="image/jpg,image/svg,image/png"
                                                                    onChange={handleFileChange}
                                                                />
                                                            </Box>

                                                            {/* REMOVE BUTTON */}
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleRemove}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 8,
                                                                    right: 8,
                                                                    bgcolor: 'rgba(255,255,255,0.9)',
                                                                    '&:hover': { bgcolor: '#fff' },
                                                                }}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                )}

                                                {errors.signature && (
                                                    <Typography
                                                        color="error"
                                                        variant="caption"
                                                        display="block"
                                                        mt={1}
                                                        textAlign="center"
                                                    >
                                                        {errors.signature.message}
                                                    </Typography>
                                                )}
                                            </>
                                        );
                                    }}
                                />

                            </Box>
                        </Stack>
                    </Box>
                </Box>

                <Divider />

                {/* ACTIONS */}
                <DialogActions
                    sx={{
                        px: 3,
                        py: 2.5,
                        justifyContent: 'flex-end',
                        gap: 2,
                    }}
                >
                    <Button onClick={onClose} color="inherit">
                        Cancel
                    </Button>

                    <LoadingButton
                        type="submit"
                        loading={isSubmitting}
                        variant="contained"
                        size="large"
                        sx={{ minWidth: 170, borderRadius: 2 }}
                    >
                        {editData ? 'Update Signature' : 'Save Signature'}
                    </LoadingButton>
                </DialogActions>
            </FormProvider>
        </>
    );
}

